import { deepEquals } from '@rjsf/utils';
import { cloneDeep, isNull } from 'lodash';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { StringParam, useQueryParams } from 'use-query-params';

import NodeMouseNearEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-near-event';
import RendererLayoutChangeEvent from 'dashboard/components/GraphCanvasV2/events/renderer-layout-change-event';
import { NodePosition } from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import { useBackend } from 'dashboard/components/GraphModule/hooks/useBackend';
import { useEventBus } from 'dashboard/components/GraphModule/hooks/useEventBus';
import { useG6Wrapper } from 'dashboard/components/GraphModule/hooks/useG6Wrapper';
import { useGraphConfiguration } from 'dashboard/components/GraphModule/hooks/useGraphConfiguration';
import { useGraphData } from 'dashboard/components/GraphModule/hooks/useGraphData';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { hydrateInBatches } from 'dashboard/lib/utils/graph';
import {
  combineGraphUpdateMessages,
  Edge,
  G6GraphUpdateMessage,
  HashableNodeId,
  Node,
  NodeId,
  nodeIdToHashable,
  V2Data,
} from 'dashboard/models/graph';
import {
  convertServerEdgeToEdge,
  convertServerNodeToNode,
  convertServerNodeToNodeId,
  ServerQueryCursor,
  ServerSearchableField,
} from 'dashboard/models/graph-backend';
import {
  AddNodeArguments,
  GraphExplorerQueryLoadingStage,
  GraphExplorerQueryLoadingState,
} from 'dashboard/models/graph-explorer';
import {
  convertToLightlyHydratedNode,
  HydratedNode,
  LightlyHydratedNode,
} from 'dashboard/models/graph-hydration';
import {
  buildByAccountQueryConfig,
  buildByClusterQueryConfig,
  buildTraversalFilteringConfig,
  ClustersQuery,
  GraphQuery,
  GraphQueryBody,
  GraphQueryHint,
  graphQueryResponseToV2Data,
  GraphQueryStatus,
  hasTraversalFiltering,
  NHopsFromAccountQuery,
  QueryConfig,
} from 'dashboard/models/graph-query';
import {
  convertSnaphotToGraphV2Data,
  convertToPayloadEdge,
  convertToPayloadNode,
  GraphSnapshot,
  GraphSnapshotAttributes,
  PayloadNode,
  SnapshotEdge,
  SnapshotNode,
  snapshotNodeIdToGraphNodeId,
} from 'dashboard/models/graph-snapshot';
import { showMessage } from 'dashboard/redux/actions/messages';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { getNodeLimit } from 'lib/constants/graph';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

interface GraphQueryingContext {
  attemptPollQuery: (graphQueryId: string, shouldClearCanvas: boolean) => Promise<boolean>;
  canLoadMore: boolean;
  currentGraphQuery: GraphQuery;
  discardOngoingHydrations: () => void;
  getHydratedNodes: (nodeIds: NodeId[]) => Promise<Map<HashableNodeId, HydratedNode>>;
  handleExpand: (nodeIds: NodeId[]) => void;
  handleLoadMoreResults: () => void;
  handleQuery: (query: GraphQueryBody, shouldClearCanvas: boolean) => Promise<boolean>;
  handleQueryWithClearCanvas: (query: GraphQueryBody) => Promise<boolean>;
  handleQueryCancel: (callback?: () => void) => void;
  loadFromSnapshot: (snapshot: GraphSnapshot) => void;
  createSnapshotPayload: () => {
    nodes: PayloadNode[];
    edges: SnapshotEdge[];
    'seed-nodes': GraphSnapshotAttributes['collapsed-groups'];
  };
  queryConfigByAccount: QueryConfig;
  queryConfigByCluster: QueryConfig;
  queryConfigAccountConnections: QueryConfig;
  querySentCounterRef: React.MutableRefObject<number>;
  // We reuse the ServerSearchableField for now to keep it simple, but we can always change it if/when we
  // need to extend it.
  searchableFields: ServerSearchableField[];
  setCanLoadMore: (val: boolean) => void;
  setCurrentGraphQuery: (val: GraphQuery) => void;
  showLiveIndicator: boolean;
}

const emptyGraphQueryingContext: Partial<GraphQueryingContext> = {
  canLoadMore: false,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  currentGraphQuery: null,
  showLiveIndicator: false,
};

interface GraphQueryLoadingStateContext {
  queryLoadingState: GraphExplorerQueryLoadingState;
  queryHint: GraphQueryHint;
  updateQueryHint: Dispatch<SetStateAction<GraphQueryHint>>;
  updateQueryLoadingState: (stage: GraphExplorerQueryLoadingStage) => void;
  setTime?: Date;
}

const emptyGraphQueryLoadingStateContext: Partial<GraphQueryLoadingStateContext> = {
  queryLoadingState: {
    stage: GraphExplorerQueryLoadingStage.Idle,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setTime: null,
  },
};

export const GraphQueryingContext =
  createContext<Partial<GraphQueryingContext>>(emptyGraphQueryingContext);
export const GraphQueryLoadingStateContext = createContext<Partial<GraphQueryLoadingStateContext>>(
  emptyGraphQueryLoadingStateContext,
);

interface GraphQueryingProviderProps {
  children: ReactNode;
  showMessage: (...args: Parameters<typeof showMessage>) => void;
}

const GraphQueryingProvider = (props: GraphQueryingProviderProps) => {
  // we need to make sure that the graphQuery we process is the most recent one fired
  // we cannot use graphQuery.id yet as the id might not have been made yet, so we use a counter here.
  // this counter increments whenever a new query is made/cancelled
  const querySentCounterRef = useRef<number>(0);

  const { queryConfigArgs, datumTypeTokens } = useGraphConfiguration();
  const [currentGraphQuery, setCurrentGraphQuery] = useState<GraphQuery | null>(null);
  const [queryConfigByAccount, setQueryConfigByAccount] = useState<QueryConfig>();
  const [queryConfigByCluster, setQueryConfigByCluster] = useState<QueryConfig>();
  const [queryConfigAccountConnections, setQueryConfigAccountConnections] = useState<QueryConfig>(
    [],
  );
  const [searchableFields, setSearchableFields] = useState<ServerSearchableField[]>([]);
  const { graphExplorer } = useGraphExplorer();
  const g6Wrapper = useG6Wrapper();
  const backend = useBackend();
  const { currentUser, graph } = useGraphData();
  const demoLiveModeEnabled = useFeatureFlag('dashboard:graph-demo-live-mode');
  const transactionsDemoEnabled = useFeatureFlag('dashboard:graph-transactions-frontend');
  const [urlQueryParams, setUrlQueryParams] = useQueryParams({
    queryId: StringParam,
  });

  // Experimental Live Mode Indicator
  const [showLiveIndicator, setShowLiveIndicator] = useState<boolean>(false);

  useEffect(() => {
    setQueryConfigByAccount(
      buildByAccountQueryConfig(
        queryConfigArgs,
        graphExplorer.getNodeRenderConfig,
        transactionsDemoEnabled,
      ),
    );
  }, [queryConfigArgs, graphExplorer, transactionsDemoEnabled]);

  useEffect(() => {
    setQueryConfigByCluster(
      buildByClusterQueryConfig(queryConfigArgs, graphExplorer.getNodeRenderConfig),
    );
  }, [queryConfigArgs, graphExplorer]);

  useEffect(() => {
    setQueryConfigAccountConnections(
      // TODO(kerry): Factor in custom datum types and properties here too.
      buildTraversalFilteringConfig(queryConfigArgs, graphExplorer.getNodeRenderConfig),
    );
  }, [queryConfigArgs, graphExplorer]);

  useEffect(() => {
    setSearchableFields(graph?.attributes['searchable-fields'] || []);
  }, [graph]);

  const [queryLoadingState, unwrappedSetQueryLoadingState] =
    useState<GraphExplorerQueryLoadingState>({
      stage: GraphExplorerQueryLoadingStage.Idle,
      setTime: new Date(),
    });

  const [queryHint, updateQueryHint] = useState<GraphQueryHint | null>(null);
  const onQueryPolled = (query: GraphQuery) => {
    updateQueryHint(query.attributes['query-hint']);
  };

  const updateQueryLoadingState = useCallback(
    (stage: GraphExplorerQueryLoadingStage, query: GraphQuery | null = null) => {
      updateQueryHint(query ? query.attributes['query-hint'] : null);
      unwrappedSetQueryLoadingState({ stage, setTime: new Date() });
    },
    [updateQueryHint, unwrappedSetQueryLoadingState],
  );

  const handleQueryCancel = (callback?: () => void) => {
    // increment querySentCounter so that if the response of the cancelled query comes back,
    // we know to discard the result as the counters are now misaligned
    querySentCounterRef.current++;

    if (callback) {
      callback();
    }

    updateQueryLoadingState(GraphExplorerQueryLoadingStage.Idle);
  };

  // -----------------------------------------------------------------------------------------
  // Initial layout.
  // -----------------------------------------------------------------------------------------
  const eventBus = useEventBus();

  const handleLayoutChange = useCallback(() => {
    // only set stage to completed if after a layoutchange during a query and not a layoutchange
    // after expanding a node
    const currentQuerySent = querySentCounterRef.current;
    if (queryLoadingState.stage === GraphExplorerQueryLoadingStage.RenderingQueryResult) {
      updateQueryLoadingState(GraphExplorerQueryLoadingStage.Completed);
      // Store currentQuerySent for this race condition: loading stage set to completed,
      // user cancels query, begins another query, then the setTimeout sets it back to Idle.
      setTimeout(() => {
        if (currentQuerySent === querySentCounterRef.current) {
          updateQueryLoadingState(GraphExplorerQueryLoadingStage.Idle);
        }
      }, 500);
    }
  }, [queryLoadingState, updateQueryLoadingState]);

  useEffect(() => {
    if (eventBus) {
      eventBus.addEventListener(RendererLayoutChangeEvent.type, handleLayoutChange);
    }

    return () => {
      eventBus.removeEventListener(RendererLayoutChangeEvent.type, handleLayoutChange);
    };
  });

  // during unmount, increment the querySentCounter to ensure that polling stops on unmount
  useEffect(() => {
    return () => {
      querySentCounterRef.current++;
    };
  }, []);

  // -----------------------------------------------------------------------------------------
  // Node hydration
  // -----------------------------------------------------------------------------------------

  // A cache for the in-flight node hydration requests, to ensure that we don't send more than one at once.
  const hydrationInProgress = useRef<Set<HashableNodeId>>(new Set());

  // We do not want to set hydration data if the hydrated node is stale
  // Example: In a case card, we hover over a node, trigger a node hydration, then switch to viewing a snapshot.
  // We do not want to save the hydrated node as the data is stale.
  const hydrationNonceRef = useRef<number>(0);
  // Returns hydrated nodes for given nodeIds. For those that have not been hydrated yet, will batch node ids
  // and make call(s) to the server
  const getHydratedNodes = useCallback(
    (
      nodeIds: NodeId[],
      includeAccountFields = false,
    ): Promise<Map<HashableNodeId, HydratedNode>> => {
      const currentNonce = hydrationNonceRef.current;
      const alreadyHydratedNodes: Map<HashableNodeId, HydratedNode> = new Map();
      const nodeIdsToHydrateFromServer: NodeId[] = [];

      const hasAccountFieldsData = (node: HydratedNode) => {
        if (node.nodeType === 'account' && includeAccountFields) {
          return !isNull(node.hydrationData['fields-data']);
        }

        return true;
      };

      nodeIds.forEach((nodeId) => {
        const existingHydrationData = graphExplorer.getHydrationData(nodeId);
        if (existingHydrationData && hasAccountFieldsData(existingHydrationData)) {
          alreadyHydratedNodes.set(nodeIdToHashable(nodeId), existingHydrationData);
        } else {
          if (graphExplorer.getCollapsedPairwiseNodeById(nodeId.value)) {
            nodeIdsToHydrateFromServer.push(
              ...graphExplorer.getPairwiseMemberNodeIds(nodeId.value),
            );
          } else {
            nodeIdsToHydrateFromServer.push(nodeId);
          }
        }
      });

      const beforeCallback = (batch: NodeId[]) => {
        batch.forEach((nodeId) => hydrationInProgress.current.add(nodeIdToHashable(nodeId)));
        return false;
      };

      const afterCallback = (batch: NodeId[], result: HydratedNode[]) => {
        batch.forEach((nodeId, idx) => {
          // do not update hydrated node if not on the same set of hydration data
          if (hydrationNonceRef.current === currentNonce) {
            graphExplorer.setHydrationData(nodeId, result[idx]);
          }
          hydrationInProgress.current.delete(nodeIdToHashable(nodeId));
        });
      };

      return new Promise((resolve, _) => {
        hydrateInBatches(
          nodeIdsToHydrateFromServer,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          graph.id,
          backend,
          graphExplorer,
          beforeCallback,
          afterCallback,
          includeAccountFields,
        ).then((data) => {
          resolve(new Map([...alreadyHydratedNodes, ...data]));
        });
      });
    },
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    [backend, graphExplorer, graph.id],
  );

  const discardOngoingHydrations = () => {
    // all hydrations with the previous nonce will be discarded upon completion
    hydrationNonceRef.current++;
  };

  const handleNodeMouseNear = useCallback(
    (e: NodeMouseNearEvent) => {
      const { graphTarget: nodes } = e;
      getHydratedNodes(
        nodes
          .map((node) => node.id)
          .filter((nodeId) => !hydrationInProgress.current.has(nodeIdToHashable(nodeId))),
      );
    },
    [getHydratedNodes],
  );

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    eventBus.addEventListener(NodeMouseNearEvent.type, handleNodeMouseNear);

    return () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      eventBus.removeEventListener(NodeMouseNearEvent.type, handleNodeMouseNear);
    };
  }, [handleNodeMouseNear, eventBus]);

  const lightlyHydrateQueryResults = useCallback(
    async (nodeIds: NodeId[]): Promise<Map<HashableNodeId, LightlyHydratedNode>> => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return backend.lightlyHydrate({ nodeIds, graphId: graph.id }).then((response) => {
        const lightHydrationData: Map<HashableNodeId, LightlyHydratedNode> = new Map(
          nodeIds.map((nodeId, index) => {
            const lightlyHydratedNode = convertToLightlyHydratedNode(
              graphExplorer.getDatumTypeByToken(nodeId.token),
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              response['lightly-hydrated-nodes'][index],
            );
            return [nodeIdToHashable(nodeId), lightlyHydratedNode];
          }),
        );
        return lightHydrationData;
      });
    },
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    [backend, graphExplorer, graph.id],
  );

  // -----------------------------------------------------------------------------------------
  // Progressive loading.
  // -----------------------------------------------------------------------------------------
  const [canLoadMore, setCanLoadMore] = useState<boolean>(false);
  const [nextCursor, setNextCursor] = useState<ServerQueryCursor>('');

  const canvasUpdateCallback = useCallback(
    (message: G6GraphUpdateMessage) => {
      g6Wrapper.updateGraph(message);
    },
    [g6Wrapper],
  );

  // NOTE: We should refactor this method to also make the downstream call to graph/demo backend.
  // The initial implementation was done through redux actions, however. Hence, why we only
  // construct the payload here.
  const createSnapshotPayload = useCallback(() => {
    return {
      nodes: graphExplorer.getAllNodes().map((node) => {
        return convertToPayloadNode(
          node,
          g6Wrapper.nodeBBoxByNodeId(node.id),
          graphExplorer.getLightlyHydratedData(node.id),
        );
      }),
      edges: graphExplorer.getAllEdges().map(convertToPayloadEdge),
      // NOTE: Currently relying on `featured` to determine seed-node coloring.
      'seed-nodes': [],
      // TODO: Support storing collapsed groups in the future.
      'collapsed-groups': [],
    };
  }, [graphExplorer, g6Wrapper]);

  const loadFromSnapshot = useCallback(
    (snapshot: GraphSnapshot) => {
      const v2Data = convertSnaphotToGraphV2Data(snapshot);
      const lightHydrationData: Map<HashableNodeId, LightlyHydratedNode> = new Map();
      snapshot.attributes.nodes.forEach((node: SnapshotNode) => {
        if (node['lightly-hydrated-values']) {
          lightHydrationData.set(
            nodeIdToHashable(snapshotNodeIdToGraphNodeId(node.id)),
            convertToLightlyHydratedNode(
              graphExplorer.getDatumTypeByToken(node.id['datum-type-token']),
              node['lightly-hydrated-values'],
            ),
          );
        }
      });

      const nodePositions: { [nodeId: HashableNodeId]: NodePosition } = {};
      snapshot.attributes.nodes.forEach((node: SnapshotNode) => {
        if (node.rendering.x && node.rendering.y) {
          nodePositions[nodeIdToHashable(snapshotNodeIdToGraphNodeId(node.id))] = {
            x: node.rendering.x,
            y: node.rendering.y,
          };
        }
      });

      const message = graphExplorer.setData(v2Data, lightHydrationData, nodePositions);
      g6Wrapper.setData(message);
    },
    [g6Wrapper, graphExplorer],
  );

  const handleCompletedGraphQueryResponse = useCallback(
    async (currentQuerySent: number, graphQuery: GraphQuery, shouldClearCanvas: boolean) => {
      const response = graphQuery.attributes.result;
      const v2Data = graphQueryResponseToV2Data(response);
      const { edges, nodes, nonfetchedEdgeCounts } = v2Data;

      // End early if no results found in the query.
      if (nodes.length === 0) {
        updateQueryLoadingState(GraphExplorerQueryLoadingStage.Idle);

        if (canLoadMore) {
          // Hide the progressive loading, if we hit the end.
          setNextCursor('');
          setCanLoadMore(false);
        }

        if (shouldClearCanvas) {
          // override the query ID in the URL, if it is a new query.
          setUrlQueryParams({ queryId: graphQuery.id });

          // canvas can be empty because results have been redacted, or the query result is empty
          if (graphQuery.attributes['redacted-at']) {
            props.showMessage('Results have been previously redacted.', MessageType.Notice, false);
          } else {
            props.showMessage('No results found with this query.', MessageType.Notice, false);
          }

          const message = graphExplorer.setData(v2Data, new Map());
          g6Wrapper.setData(message);
        }
        setCurrentGraphQuery(graphQuery);
        return true;
      }

      // Currently, we don't support using both traversal filtering and progressive loading at the same time.
      // We should remove the traversal filtering check if/when we support querying with both features together.
      if (nodes.length >= getNodeLimit(graphQuery) && !hasTraversalFiltering(graphQuery)) {
        // Update load more state.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setNextCursor(response['next-cursor']);
        setCanLoadMore(true);
      } else {
        // Update load more state.
        setNextCursor('');
        setCanLoadMore(false);
      }

      const nodeIds: NodeId[] = nodes.map((node) => node.id);

      // end prematurely if query is cancelled
      if (querySentCounterRef.current !== currentQuerySent) {
        return false;
      }

      // Kick off a call to do light hydration, and render after all of the light hydration data is fetched.
      const lightHydrationData = await lightlyHydrateQueryResults(nodeIds);

      updateQueryLoadingState(GraphExplorerQueryLoadingStage.RenderingQueryResult);

      if (shouldClearCanvas) {
        const message = graphExplorer.setData(v2Data, lightHydrationData);
        // Set the G6 state.
        g6Wrapper.setData(message);
      } else {
        const addNodeArgumentsList: AddNodeArguments[] = [];

        const newNodes = Array.from(nodes.values()).filter((node) => {
          return !graphExplorer.containsNode(node.id);
        });
        let newNodePositions: NodePosition[];
        if (graphQuery.attributes.params.type === 'experimental_live_query') {
          newNodePositions = g6Wrapper.getExperimentalLiveNodePositions(newNodes, edges);
        } else {
          newNodePositions = g6Wrapper.getProgressiveLoadingNodePositions(newNodes.length);
        }
        newNodes.forEach((node, index) => {
          const nonfetchedEdgeCount = nonfetchedEdgeCounts.get(nodeIdToHashable(node.id)) ?? 0;
          const addNodeArguments: AddNodeArguments = {
            node,
            nonfetchedEdgeCount,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            lightlyHydratedNode: lightHydrationData.get(nodeIdToHashable(node.id)),
            // We seed the initial positions close to each other, so that they'll experience some repulsion.
            position: newNodePositions[index],
          };
          addNodeArgumentsList.push(addNodeArguments);
        });

        // We add nodes in bulk, because they trigger a full graph relayout.
        const addMessages = graphExplorer.bulkAddNodesWithoutCanvasUpdate(addNodeArgumentsList);

        const newEdges: Edge[] = [];
        let allOldNodeIds: NodeId[] = [];
        edges.forEach((edge) => {
          if (graphExplorer.containsEdge(edge.id1, edge.id2)) {
            // Skip the edge if we already know about it.
            return;
          }
          newEdges.push(edge);
          const oldNodeIds = [edge.id1, edge.id2].filter(
            (oldNodeId) => !nodes.some((node) => deepEquals(oldNodeId, node.id)),
          );

          if (oldNodeIds.length >= 2) {
            throw new Error(
              `invalid edge: ${nodeIdToHashable(edge.id1)} ${nodeIdToHashable(edge.id2)}`,
            );
          }

          allOldNodeIds = [...allOldNodeIds, ...oldNodeIds];
        });

        const edgeMessages = graphExplorer.addEdgesWithoutCanvasUpdate(newEdges);
        const decrementEdgeMessages =
          graphExplorer.decrementNonfetchedEdgeCountWithoutCanvasUpdate(allOldNodeIds);

        canvasUpdateCallback(
          combineGraphUpdateMessages([...addMessages, ...edgeMessages, ...decrementEdgeMessages]),
        );
        // Force a relayout, since we just (potentially) dumped a bunch of nodes and edges onto the canvas.
        g6Wrapper.relayout();
      }

      // query is completed and graph explorer is now showing it.
      // Update the query ID if it wasn't a progressive load.
      if (shouldClearCanvas) {
        setUrlQueryParams({ queryId: graphQuery.id });
      }
      setCurrentGraphQuery(graphQuery);
      return true;
    },
    [
      canLoadMore,
      canvasUpdateCallback,
      g6Wrapper,
      graphExplorer,
      lightlyHydrateQueryResults,
      props,
      setUrlQueryParams,
      updateQueryLoadingState,
    ],
  );

  useEffect(() => {
    if (urlQueryParams.queryId != null) {
      // NOTE: This shouldn't own this
      // if beginning to load query from urlQueryParams, hide the editor
      // TODO: Bring this back
      // graphQueryEditorHide();
      querySentCounterRef.current++;
      updateQueryLoadingState(GraphExplorerQueryLoadingStage.LoadingGraph);
    }
  }, []);

  // -----------------------------------------------------------------------------------------
  // Query Utils
  // -----------------------------------------------------------------------------------------
  const onError = (error: Error) => {
    if (error instanceof ApiResponseError && isPermissionsError(error)) {
      props.showMessage(permissionsErrorMessage(error), MessageType.Failure, false);
    } else {
      props.showMessage(
        'Failed to load graph, please reload to try again',
        MessageType.Failure,
        false,
      );
    }
    updateQueryLoadingState(GraphExplorerQueryLoadingStage.Idle);
  };
  const onNetworkError = () => {
    props.showMessage(
      'We are experiencing network issues. Please try again.',
      MessageType.Notice,
      false,
    );
    updateQueryLoadingState(GraphExplorerQueryLoadingStage.Idle);
  };
  const onQueryError = (graphQuery: GraphQuery) => {
    if (graphQuery.attributes.status === GraphQueryStatus.TimedOut) {
      props.showMessage(
        `Query ${graphQuery.id} has timed out. Try searching over a smaller time period; e.g., 1 day.`,
        MessageType.Failure,
      );
    } else if (
      graphQuery.attributes['error-cause'] === 'GraphQuery::ExceededMaxTraversalAttempts'
    ) {
      props.showMessage(
        `Query ${graphQuery.id} has timed out. Try searching with less specific filters.`,
        MessageType.Failure,
      );
    } else {
      props.showMessage(`Query ${graphQuery.id} errored`, MessageType.Failure);
    }
    updateQueryLoadingState(GraphExplorerQueryLoadingStage.Idle);
  };
  const onQuerySubmitted = (query: GraphQuery) => {
    updateQueryLoadingState(GraphExplorerQueryLoadingStage.WaitingForGraphQueryResult, query);
  };

  // -----------------------------------------------------------------------------------------
  // Cluster query.
  // -----------------------------------------------------------------------------------------
  const handleClusterQuery = useCallback(
    async (clustersQuery: ClustersQuery, shouldClearCanvas: boolean) => {
      if (graph == null) {
        // should not happen naturally as the query button should be disabled, but good to check
        props.showMessage(
          'Cannot make graph queries until graph environment loads',
          MessageType.Notice,
        );
        return false;
      }

      // increment counter as it is the start of a query
      querySentCounterRef.current++;
      const currentQuerySent = querySentCounterRef.current;
      const shouldContinue = () => querySentCounterRef.current === currentQuerySent;
      updateQueryLoadingState(GraphExplorerQueryLoadingStage.SendingQuery);

      clustersQuery['min-cluster-size'] = clustersQuery['min-cluster-size'] || 2;
      clustersQuery['num-hops'] = clustersQuery['num-hops'] || 10;

      const graphQuery = await backend.runQueryAndPoll({
        query: clustersQuery,
        graphId: graph.id,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        creatorId: currentUser.id,
        creatorType: 'User',
        shouldContinue,
        onError,
        onNetworkError,
        onQueryError,
        onQuerySubmitted,
        onQueryPolled,
      });

      if (graphQuery?.attributes?.status === GraphQueryStatus.Completed) {
        updateQueryLoadingState(GraphExplorerQueryLoadingStage.HydratingGraphNodes);
        return await handleCompletedGraphQueryResponse(
          currentQuerySent,
          graphQuery,
          shouldClearCanvas,
        );
      }
      return false;
    },
    [backend, handleCompletedGraphQueryResponse, props, updateQueryLoadingState],
  );

  // -----------------------------------------------------------------------------------------
  // N Hops query.
  // -----------------------------------------------------------------------------------------
  const handleNHopsQuery = useCallback(
    async (nHopsQuery: NHopsFromAccountQuery, shouldClearCanvas: boolean) => {
      if (graph == null) {
        // should not happen naturally as the query button should be disabled, but good to check
        props.showMessage(
          'Cannot make graph queries until graph environment loads',
          MessageType.Notice,
        );
        return false;
      }

      // increment counter as it is the start of a query
      querySentCounterRef.current++;
      const currentQuerySent = querySentCounterRef.current;
      const shouldContinue = () => querySentCounterRef.current === currentQuerySent;
      updateQueryLoadingState(GraphExplorerQueryLoadingStage.SendingQuery);

      const graphQuery = await backend.runQueryAndPoll({
        query: nHopsQuery,
        graphId: graph.id,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        creatorId: currentUser.id,
        creatorType: 'User',
        shouldContinue,
        onError,
        onNetworkError,
        onQueryError,
        onQuerySubmitted,
        onQueryPolled,
      });

      if (graphQuery?.attributes?.status === GraphQueryStatus.Completed) {
        updateQueryLoadingState(GraphExplorerQueryLoadingStage.HydratingGraphNodes);
        return await handleCompletedGraphQueryResponse(
          currentQuerySent,
          graphQuery,
          shouldClearCanvas,
        );
      }
      return false;
    },
    [backend, handleCompletedGraphQueryResponse, props, updateQueryLoadingState],
  );

  const handleQuery = useCallback(
    async (query: GraphQueryBody, shouldClearCanvas: boolean) => {
      if (graph == null) {
        // should not happen naturally as the query button should be disabled, but good to check
        props.showMessage(
          'Cannot make graph queries until graph environment loads',
          MessageType.Notice,
        );
        return false;
      }

      // increment counter as it is the start of a query
      querySentCounterRef.current++;
      const currentQuerySent = querySentCounterRef.current;
      const shouldContinue = () => querySentCounterRef.current === currentQuerySent;
      updateQueryLoadingState(GraphExplorerQueryLoadingStage.SendingQuery);

      const graphQuery = await backend.runQueryAndPoll({
        query,
        graphId: graph.id,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        creatorId: currentUser.id,
        creatorType: 'User',
        shouldContinue,
        onError,
        onNetworkError,
        onQueryError,
        onQuerySubmitted,
        onQueryPolled,
      });

      if (graphQuery?.attributes?.status === GraphQueryStatus.Completed) {
        updateQueryLoadingState(GraphExplorerQueryLoadingStage.HydratingGraphNodes);
        return await handleCompletedGraphQueryResponse(
          currentQuerySent,
          graphQuery,
          shouldClearCanvas,
        );
      }
      return false;
    },
    [backend, handleCompletedGraphQueryResponse, props, updateQueryLoadingState],
  );

  const handleQueryWithClearCanvas = async (query: GraphQueryBody) => {
    return await handleQuery(query, true);
  };

  // -----------------------------------------------------------------------------------------
  // Query Polling
  // -----------------------------------------------------------------------------------------
  const attemptPollQuery = useCallback(
    async (queryId: string, shouldClearCanvas: boolean) => {
      // increment counter as it is the start of a query
      querySentCounterRef.current++;
      const currentQuerySent = querySentCounterRef.current;
      const shouldContinue = () => querySentCounterRef.current === currentQuerySent;

      const graphQuery = await backend.pollContinuously({
        graphQueryId: queryId,
        shouldContinue,
        onError,
        onNetworkError,
        onQueryError,
        onQueryPolled,
        cancelQueryIfStoppedPolling: false,
      });

      if (graphQuery?.attributes?.status === GraphQueryStatus.Completed) {
        updateQueryLoadingState(GraphExplorerQueryLoadingStage.HydratingGraphNodes);
        setCurrentGraphQuery(graphQuery);
        return await handleCompletedGraphQueryResponse(
          currentQuerySent,
          graphQuery,
          shouldClearCanvas,
        );
      }
      return false;
    },
    [backend, handleCompletedGraphQueryResponse, props, updateQueryLoadingState],
  );

  // -----------------------------------------------------------------------------------------
  // More progressive loading.
  // -----------------------------------------------------------------------------------------

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const handleLoadMoreResults = useCallback(async (): Promise<boolean> => {
    // Important: We need to use `cloneDeep` rather than JSON.parse(JSON.stringify(...)) here,
    //            since the current graph query may have Moment objects that don't make the round
    //            trip properly.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const query: GraphQueryBody = cloneDeep(currentGraphQuery).attributes.params as GraphQueryBody;
    query['known-node-ids'] = graphExplorer.getNodeIds();
    SegmentClient.sendSegmentTrackEvent('Graph - Progressive Load');

    switch (query.type) {
      case 'by_cluster': {
        query['start-cursor'] = nextCursor;
        return handleClusterQuery(query, false);
      }
      case 'n_hops_from_account': {
        return handleNHopsQuery(query, false);
      }
    }
  }, [currentGraphQuery, graphExplorer, nextCursor, handleClusterQuery]);

  // -----------------------------------------------------------------------------------------
  // Live loading
  // NOTE: This feature is completely experimental and not hooked up to a backend at all. Because of that,
  // this is likely to get removed or the interface is likely to change.
  // -----------------------------------------------------------------------------------------
  const handleLiveLoadMoreResults = useCallback(async (): Promise<boolean> => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (graph.id == null || demoLiveModeEnabled) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return;
    }

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const newGraphQuery = await backend.liveQuery(graph.id);

    querySentCounterRef.current++;
    const currentQuerySent = querySentCounterRef.current;

    return await handleCompletedGraphQueryResponse(currentQuerySent, newGraphQuery, false);
  }, [backend, graph, querySentCounterRef, demoLiveModeEnabled, handleCompletedGraphQueryResponse]);

  const [ctrlPressed, setCtrlPressed] = useState(false);
  const showLiveIndicatorTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (!demoLiveModeEnabled) {
      return;
    }

    if (ctrlPressed) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      showLiveIndicatorTimer.current = setTimeout(() => {
        setShowLiveIndicator(true);
      }, 500);
    } else {
      if (showLiveIndicatorTimer.current != null) {
        clearTimeout(showLiveIndicatorTimer.current);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        showLiveIndicatorTimer.current = null;
        setShowLiveIndicator(false);
      }
    }
  }, [ctrlPressed]);

  useEffect(() => {
    if (!demoLiveModeEnabled) {
      return;
    }

    const handleCtrlKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        setCtrlPressed(true);
      }
    };

    const handleCtrlKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        setCtrlPressed(false);
      }
    };

    const handleLKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'KeyL') {
        if (ctrlPressed) {
          handleLiveLoadMoreResults();
        }
      }
    };

    window.addEventListener('keyup', handleCtrlKeyUp);
    window.addEventListener('keydown', handleCtrlKeyDown);
    window.addEventListener('keydown', handleLKeyDown);

    return () => {
      window.removeEventListener('keyup', handleCtrlKeyUp);
      window.removeEventListener('keydown', handleCtrlKeyDown);
      window.removeEventListener('keydown', handleLKeyDown);
    };
  });

  // -----------------------------------------------------------------------------------------
  // Node expansion
  // -----------------------------------------------------------------------------------------
  const handleExpand = (nodeIds: NodeId[]) => {
    SegmentClient.sendSegmentTrackEvent('Graph - Expand');

    // Dimmed nodes and already expanded nodes are not expandable
    const expandableNodeIds = nodeIds.filter((nodeId: NodeId) => {
      const node = graphExplorer.getNodeById(nodeIdToHashable(nodeId));
      return !node.state.dimmed && node.state.nonfetchedEdgeCount;
    });
    for (const nodeId of expandableNodeIds) {
      graphExplorer.setNodeLoading(nodeId, true);
    }

    // Hide the "load more" box, if it is shown.
    setNextCursor('');
    setCanLoadMore(false);

    expandableNodeIds.forEach((nodeToExpand) => {
      backend
        .expand({
          nodeId: nodeToExpand,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          graphId: graph.id,
          expandDatumTypes: datumTypeTokens,
          existingNodeIds: graphExplorer.getNodeIds(),
        })
        .then((response) => {
          if (g6Wrapper == null) {
            return;
          }
          const nodes: Node[] = [];
          const edges: Edge[] = [];

          const serverNodes = response['nodes-and-edges'].nodes;
          const serverEdges = response['nodes-and-edges'].edges;
          const serverExpandableNodes = response['expandable-nodes'];

          serverNodes.forEach((serverNode) => {
            nodes.push(convertServerNodeToNode(serverNode));
          });
          serverEdges.forEach((serverEdge) => {
            edges.push(convertServerEdgeToEdge(serverEdge));
          });
          const newNodes: Set<HashableNodeId> = new Set(
            nodes
              .filter((node) => !graphExplorer.containsNode(node.id))
              .map((node) => nodeIdToHashable(node.id)),
          );
          const newNodeCount = newNodes.size;
          const expansionNodePositions = g6Wrapper.getExpansionNodePositions(
            nodeToExpand,
            newNodeCount,
          );
          if (expansionNodePositions.length !== newNodeCount) {
            throw new Error('Wrong number of expansion nodes');
          }
          let numberOfExpansionNodesUsed = 0;
          const expandableNodes: Map<HashableNodeId, number> = new Map(
            serverExpandableNodes.map((serverNodeExpandability) => [
              nodeIdToHashable(convertServerNodeToNodeId(serverNodeExpandability.node)),
              serverNodeExpandability['nonfetched-edge-count'],
            ]),
          );

          lightlyHydrateQueryResults(nodes.map((node) => node.id)).then((lightHydrationData) => {
            const addNodeArgumentsList: AddNodeArguments[] = [];

            nodes.forEach((node) => {
              if (!graphExplorer.containsNode(node.id)) {
                const nonfetchedEdgeCount = expandableNodes.get(nodeIdToHashable(node.id)) ?? 0;
                const addNodeArguments: AddNodeArguments = {
                  node,
                  nonfetchedEdgeCount,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  lightlyHydratedNode: lightHydrationData.get(nodeIdToHashable(node.id)),
                  position: expansionNodePositions[numberOfExpansionNodesUsed],
                };
                node.state.new = true;
                addNodeArgumentsList.push(addNodeArguments);
                numberOfExpansionNodesUsed += 1;
              }
            });

            // We clear the node selection, in case of weirdness from double-click-to-expand.
            // TODO(kerry): Revisit and check if this decision makes sense.
            graphExplorer.setSelectables([]);

            const newEdges = edges.filter(
              (edge) => !graphExplorer.containsEdge(edge.id1, edge.id2),
            );

            // We add nodes and edges in bulk, because they trigger a full graph relayout.
            graphExplorer.bulkAddNodesAndEdges(addNodeArgumentsList, newEdges);

            // Update the nonfetched edge counts accordingly.
            newEdges.forEach((edge) => {
              const oldNodeIds = [edge.id1, edge.id2].filter(
                (nodeId) => !newNodes.has(nodeIdToHashable(nodeId)),
              );
              if (oldNodeIds.length >= 2) {
                throw new Error(
                  `invalid edge: ${nodeIdToHashable(edge.id1)} ${nodeIdToHashable(edge.id2)}`,
                );
              }

              graphExplorer.decrementNonfetchedEdgeCount(oldNodeIds);
            });

            nodeIds.forEach((nodeId) => {
              graphExplorer.setNodeLoading(nodeId, false);
            });

            // Force a relayout, since we just (potentially) added a bunch of nodes and edges to the canvas.
            // We intentionally don't update to GraphExplorerQueryLoadingStage.RenderingQueryResult because
            // we don't want the progress bar to appear during expansions.
            g6Wrapper.relayout();
          });
        });
    });
  };

  // Clear all data because the backend has changed to avoid weird edge cases
  useEffect(() => {
    const emptyData: V2Data = {
      edges: [],
      nodes: [],
      nonfetchedEdgeCounts: new Map(),
      seedNodes: [],
    };

    setNextCursor('');
    setCanLoadMore(false);

    const message = graphExplorer.setData(emptyData, new Map());
    if (g6Wrapper.graph) {
      g6Wrapper.setData(message);
    }
  }, [backend, g6Wrapper, graphExplorer]);

  return (
    <GraphQueryingContext.Provider
      value={{
        attemptPollQuery,
        canLoadMore,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentGraphQuery,
        discardOngoingHydrations,
        getHydratedNodes,
        handleExpand,
        handleLoadMoreResults,
        handleQuery,
        handleQueryWithClearCanvas,
        handleQueryCancel,
        loadFromSnapshot,
        createSnapshotPayload,
        queryConfigByAccount,
        queryConfigByCluster,
        queryConfigAccountConnections,
        querySentCounterRef,
        searchableFields,
        setCanLoadMore,
        setCurrentGraphQuery,
        showLiveIndicator,
      }}
    >
      <GraphQueryLoadingStateContext.Provider
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        value={{ queryLoadingState, queryHint, updateQueryLoadingState, updateQueryHint }}
      >
        {props.children}
      </GraphQueryLoadingStateContext.Provider>
    </GraphQueryingContext.Provider>
  );
};

export default GraphQueryingProvider;
